'use client';

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { DynamicInput, CRMLookup, Box, PulseRadio } from 'src/components';
import { Dialog, PulseButton } from 'src/components/PulseUI';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useLayoutControl } from 'src/context/layout';
import { usePushToCRMMutation, useGetUserInputOptionsQuery, useGetMappingsQuery } from 'src/api/redux';
import { entityTypeSlugMap } from 'src/components/Settings/constants';

// FLOW 3: One mapping, one or more options - Dialog Steps
// FLOW 4: Multiple mappings, 0 or more options - Dialog, then based on mapping, either submit or show the flow
interface PushToCRMDialogProps {
  open: boolean;
  onClose: (event: any, reason?: string) => void;
  provider: MedScout.Opportunity;
  integration: string;
}

const PushToCRMDialog = ({
  open,
  onClose,
  provider,
  integration,
}: PushToCRMDialogProps) => {
  const { type } = useParams<{ type: string }>();

  const normalizedType = type?.toLowerCase();
  const contentType =
    normalizedType && entityTypeSlugMap[normalizedType]
      ? entityTypeSlugMap[normalizedType]
      : provider?.content_type?.toLowerCase();

  const log = useLogging();
  const { setNotification } = useNotification();
  const { setPushDuplicateToCRM } = useLayoutControl();

  const { data: allMappings } = useGetMappingsQuery({
    sort: 'label',
    order: 'asc',
    page: 0,
    pageSize: 100,
  });

  // Gets mappings for specific provider type, removing used mappings
  const mappings = useMemo(() => {
    const unusedMappings = allMappings?.results
      ?.filter((mapping) => {
        return !provider?.crm_links?.some(
          (link) => link.mapping_id === mapping.id
        );
      })
      ?.filter(
        (mapping) =>
          mapping?.medscout_object_type?.toLowerCase() === contentType &&
          mapping?.enabled &&
          mapping?.crm?.toLowerCase() === integration?.toLowerCase()
      );

    return unusedMappings;
  }, [allMappings, integration, contentType, provider]);

  const [mappingType, setMappingType] = useState<MedScout.Mapping | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  const [pushToCRM, { isLoading }] = usePushToCRMMutation();

  const mapping = mappings?.find((item) => item.id === mappingType?.id);
  const isThirdParty = mapping?.crm?.toLowerCase() === 'third_party';

  const { data: userInputOptions } = useGetUserInputOptionsQuery(
    {
      mappingId: mappingType?.id,
      page: 0,
      pageSize: 100,
    },
    { skip: !mappingType?.id }
  );

  const {
    handleSubmit,
    reset,
    control,
    formState: { isValid },
  } = useForm({
    mode: 'all',
  });

  useEffect(() => {
    if (!mappings || activeStep !== 0) return;
    const handleSingleMapping = () => {
      if (mappings?.length === 1) {
        setMappingType(mappings[0]);
        handleNext();
      }
      return;
    };

    handleSingleMapping();
  }, [activeStep, mappings]);

  useEffect(() => {
    if (!mappingType) return;
    reset();
  }, [mappingType]);

  // Determine all options are hidden
  const isAllHidden = userInputOptions?.results?.every(
    (option) => option?.hidden === true
  );

  const isLastStep = useCallback(() => {
    // For my future self: if activestep equals 1 and there are no options, or all options are hidden, that means there are no further steps needed.
    return (
      activeStep === 1 || userInputOptions?.results?.length === 0 || isAllHidden
    );
  }, [activeStep, isAllHidden, userInputOptions?.results?.length]);

  const handleNext = () => {
    setActiveStep((prev) => {
      if (!isLastStep()) {
        return prev + 1;
      }
    });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    log.event('Push to CRM - Back', {
      ...provider,
    });
  };

  const handleCancel = (e) => {
    setActiveStep(0);
    setMappingType(null);
    reset();
    onClose(e, 'backdropClick');
  };

  const onSubmit = async (data: any, e) => {
    try {
      // We need to include the hidden fields in the payload if isAllHidden is true
      const fieldsToPush = isAllHidden
        ? userInputOptions?.results?.reduce((acc, option) => {
            if (!option?.hidden) return acc;
            acc[option?.crm_api_code] = option?.extra?.default_value || '';
            return acc;
          }, {})
        : data;

      const response = await pushToCRM({
        object_type: mapping.medscout_object_type,
        mapping: mapping.id,
        fields: fieldsToPush || {},
        object_id: provider?.provider_id,
      }).unwrap();

      if (response.duplicate) {
        const duplicateData = {
          mapping: mapping.id,
          object_type: mapping.medscout_object_type,
          object_id: provider?.provider_id,
          ...response,
        };
        setPushDuplicateToCRM(duplicateData);
        handleCancel(e);
        return;
      }
      setNotification({
        title: isThirdParty
          ? 'Push to CRM Initiated'
          : 'Push to CRM successful',
        message: isThirdParty
          ? 'Successfully initiated push to CRM. This may take a few minutes.'
          : 'Successfully pushed to CRM.',
        type: 'success',
      });
      handleCancel(e);
      log.event('Push to CRM', {
        ...provider,
      });
    } catch (err: any) {
      setNotification({
        title: 'Push to CRM failed',
        message:
          err?.error_message || 'Something went wrong, please try again later.',
        type: 'error',
      });

      log.exception(`Error pushing to CRM: ${err}`);
    }
  };

  // move all Boolean options to the end
  const newOptions = useMemo(() => {
    if (!userInputOptions?.results) return [];
    return userInputOptions.results
      .slice()
      .sort((opt) => (opt.input_type === 'BOOLEAN' ? 1 : -1));
  }, [userInputOptions?.results]);

  return (
    <Dialog
      isOpen={open}
      onClose={() => onClose({}, 'backdropClick')}
      size="xl"
      showCloseButton
      data-testid="push-to-crm-dialog"
    >
      <Dialog.Header className="border-b border-neutral-200">
        <span
          className="text-lg font-semibold text-neutral-800"
          data-testid="push-to-crm-dialog-title"
        >
          Push to CRM
        </span>
      </Dialog.Header>
      <Dialog.Content className="p-6" data-testid="push-to-crm-content">
        {mappingType && activeStep !== 0 && (
          <Box
            className="mb-4 pb-2 border-b border-neutral-200"
            data-testid="selected-mapping-label"
          >
            <span className="text-base font-semibold">{mapping?.label}</span>
          </Box>
        )}
        <div className="flex flex-col space-y-4" data-testid="mappings-list">
          {activeStep === 0 &&
            mappings?.map((mapping, index) => {
              return (
                <div
                  key={index}
                  className="border border-neutral-300 rounded p-4 hover:bg-neutral-50 cursor-pointer"
                  onClick={() => setMappingType(mapping)}
                  data-testid={`mapping-option-${index}`}
                >
                  <div className="flex items-center">
                    <PulseRadio
                      size="md"
                      label=""
                      variant="solid"
                      color="primary"
                      checked={
                        mappingType?.crm_object_type ===
                        mapping?.crm_object_type
                      }
                      onChange={() => setMappingType(mapping)}
                    />
                    <div className="ml-2 flex flex-col">
                      <span className="font-semibold">
                        {mapping.crm_object_type}
                      </span>
                      <span className="text-sm font-medium text-neutral-700">
                        {mapping?.label}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          {activeStep === 1 &&
            newOptions?.map((option, index) => {
              if (option.input_type === 'CRM_LOOKUP') {
                const crmType = option?.extra?.crm_object_type;

                return (
                  <div
                    key={index}
                    className="mb-4"
                    data-testid={`crm-lookup-option-${index}`}
                  >
                    <span className="text-sm font-semibold text-neutral-700 mb-2 flex items-center gap-2">
                      {option?.label} (required)
                    </span>
                    <CRMLookup
                      crmType={crmType}
                      crm={mapping?.crm?.toLowerCase()}
                      option={option}
                      control={control}
                      rules={{
                        required: true,
                      }}
                    />
                  </div>
                );
              } else {
                return (
                  <div
                    key={index}
                    className={`${option?.hidden ? 'hidden' : 'mb-4'}`}
                    data-testid={`input-option-${index}`}
                  >
                    {option?.input_type !== 'BOOLEAN' && (
                      <span
                        className={`text-xs font-semibold
                        text-secondary ${
                          option?.hidden ? 'none' : 'flex items-center gap-2'
                        }`}
                      >
                        {option?.label}{' '}
                        {option?.extra?.required && '(required)'}
                      </span>
                    )}
                    <DynamicInput
                      type={option.input_type?.toLowerCase()}
                      name={option.crm_api_code?.toLowerCase()}
                      rules={{
                        required: option?.extra?.required || false,
                      }}
                      hidden={option?.hidden}
                      control={control}
                      variant="standard"
                      value={option?.extra?.default_value}
                      placeholder={
                        `${option?.extra?.default_value}` || 'e.g. 1234'
                      }
                      options={option?.extra?.options || []}
                      label={option?.label}
                    />
                  </div>
                );
              }
            })}
        </div>
      </Dialog.Content>
      <Dialog.Actions
        className="px-6 py-4 border-t border-neutral-200"
        data-testid="push-to-crm-actions"
      >
        <div className="w-full flex justify-between">
          <PulseButton
            variant="outline"
            color="neutral"
            disabled={activeStep === 0}
            onClick={handleBack}
            aria-label="Go back"
            data-testid="push-to-crm-back-button"
          >
            Back
          </PulseButton>
          <div className="flex items-center gap-3">
            <PulseButton
              variant="outline"
              color="neutral"
              aria-label="Cancel"
              data-testid="push-to-crm-cancel-button"
              onClick={(e) => {
                handleCancel(e);
                log.event('Push to CRM - Cancel', {
                  ...provider,
                });
              }}
            >
              Cancel
            </PulseButton>
            {isLastStep() ? (
              <PulseButton
                variant="solid"
                color="primary"
                loading={isLoading}
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid}
                aria-label="Submit to CRM"
                data-testid="push-to-crm-submit-button"
              >
                Submit
              </PulseButton>
            ) : (
              <PulseButton
                variant="solid"
                color="primary"
                onClick={handleNext}
                disabled={!mappingType}
                aria-label="Next step"
                data-testid="push-to-crm-next-button"
              >
                Next
              </PulseButton>
            )}
          </div>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default PushToCRMDialog;
